import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import DropdownFilterTable from 'general/components/DropdownFilterTable';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, { KTPaginationColor } from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import UIHelper from 'general/helpers/UIHelper';
import Global from 'general/utils/Global';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ModelTreeDropdown from 'features/Charge/ChargePoint/components/ModelTreeDropdown';
import ChargePointListFilterView from '../ChargePointListFilterView';
import AppConstants from 'general/constants/AppConstants';
import { dateFilter } from 'general/components/Others/CustomDateRangePicker';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

ChargePointListView.propTypes = {
  stationId: PropTypes.string,
  hasAppTab: PropTypes.bool,
  chargePointListFilter: PropTypes.object,
  onFilterChanged: PropTypes.func,
};

const defaultColumnIds = [
  'CODE',
  'NAME',
  'STATION_CODE',
  'STATION',
  'VEHICLE_TYPE',
  'OCPP',
  'PHONE',
  'MODEL',
  'TYPE',
  'STATUS',
  'POWER',
  'CONNECTOR_NUM',
  'CONNECTOR_1_STATUS',
  'CONNECTOR_2_STATUS',
  'PRICING_MODE',
  'PRICE',
  'FREE_DURATION',
  'OVER_PRICE',
  'RESERVATION_PRICE',
  'MAINTENANCE_SCHEDULE'
];

function ChargePointListView({ stationId, hasAppTab = false, chargePointListFilter, enabledEdit = true, isTestStation }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const isInvestor = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'INVESTOR';
  const isCoinvestor = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'COINVESTOR';
  const isAdminAgency = useMemo(() => {
    if (user) {
      return user?.role === 'AGENCY' && user?.agencyId === 1;
    }
    return false;
  }, [user]);
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [filter, setFilter] = useState();
  const [filterDropdown, setFilterDropdown] = useState(chargePointListFilter);
  const [filteredItems, setFilteredItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [availableTotal, setAvailableTotal] = useState(0);
  const [stationModels, setStationModels] = useState([]);
  const [stationBrands, setStationBrands] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!stationId) return;
    ChargeHelper.API.requestGetChargePointList({ stationId, limit: 9999 }).then((data) => {
      const models = _.uniqBy(
        (data?.chargePoints ?? []).filter((p) => p.chargeModel).map((p) => p.chargeModel),
        'modelId'
      );
      setStationModels(models);
      const brandIds = _.uniq(models.map((m) => m.brandId).filter(Boolean));
      ChargeHelper.API.requestGetChargeBrandList({ limit: 9999 }).then((res) => {
        setStationBrands((res.brands ?? []).filter((b) => brandIds.includes(b.brandId)));
      });
    });
  }, [stationId]);

  useEffect(() => {
    if (chargePointListFilter) {
      setFilter(chargePointListFilter);
    } else {
      setFilter({
        page: 0,
        limit: AppConfigs.defaultPageLimit,
        q: '',
        status: '',
        type: '',
        stationId: '',
        vehicleType: '',
        chargingSpeed: '',
        modelId: '',
      });
    }
  }, [chargePointListFilter]);

  useEffect(() => {
    if (!filter) return;
    const params = _.pickBy(
      {
        ...filter,
        stationId: stationId ?? filter?.stationId,
      },
      (value) => value !== null && value !== undefined && value !== ''
    );

    if (isTestStation) {
      params.isTestChargePoint = isTestStation;
    }

    ChargeHelper.API.requestGetChargePointList(params).then(async (data) => {
      const chargePoints = data?.chargePoints ?? [];
      // Fetch pricing list để merge vào từng trụ sạc
      const pricingData = await ChargeHelper.API.requestGetChargePricingList({ limit: 9999 });
      const pricings = pricingData?.pricings ?? [];
      const enrichedPoints = chargePoints.map((point) => {
        // Ưu tiên 1: pricing riêng của trụ
        const pointPricing = pricings.find((p) => p.pointId === point.pointId);
        // Ưu tiên 2: pricing mặc định của nhà điều hành (khi trụ không cấu hình riêng)
        const operatorPricing = !pointPricing
          ? pricings.find((p) => p.operatorId === point.operatorId && !p.pointId && !p.agencyId)
          : null;
        return { ...point, pricing: pointPricing ?? operatorPricing ?? point.pricing };
      });
      setFilteredItems(enrichedPoints);
      setTotal(data?.total);
    });

    ChargeHelper.API.requestGetChargePointList({
      ...params,
      status: ChargeConstants.ChargePointStatus.AVAILABLE,
    }).then((data) => {
      setAvailableTotal(data?.total);
    });
  }, [filter, stationId, isTestStation]);

  const fullColumns = useMemo(() => {
    return [
      {
        id: 'CODE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã trụ sạc
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.pointCode}>
            <span>
              {row?.pointCode}
            </span>
          </KTTooltip>
        ),
        width: '200px',
        style: tableStyle.columns.link,
      },
      {
        id: 'NAME',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Tên trụ sạc
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.name}>
            <span>
              {row?.name}
            </span>
          </KTTooltip>
        ),
        width: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATION_CODE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã trạm sạc
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.chargeStation?.stationCode}>
            <span>
              {row?.chargeStation?.stationCode}
            </span>
          </KTTooltip>
        ),
        width: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATION',
        omit: !!stationId,
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạm sạc
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.chargeStation?.name}>
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                Utils.openInNewTab(`${AppRoute.stationList}/${row?.chargeStation?.stationId ?? ''}`)
              }}
            >
              {row?.chargeStation?.name}
            </span>
          </KTTooltip>
        ),
        minWidth: '200px',
        style: tableStyle.columns.link,
      },
      {
        id: 'VEHICLE_TYPE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Loại phương tiện
          </span>
        ),
        selector: (row) =>
          row?.chargeModel?.vehicleType
            ? _.find(
              ChargeHelper.Data.vehicleTypeOptions,
              (item) => item.value === row?.chargeModel?.vehicleType
            )?.name
            : '',
        width: '130px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OCPP',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã CPID
          </span>
        ),
        selector: (row) => row?.ocppId,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PHONE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Số điện thoại
          </span>
        ),
        selector: (row) => row?.phoneNumber,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MODEL',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Dòng trụ sạc
          </span>
        ),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
            {row?.chargeModel?.name}
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'TYPE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Loại trụ sạc
          </span>
        ),
        selector: (row) => row?.type,
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạng thái trụ
          </span>
        ),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargePointStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargePointStatusColor(
                row?.status
              )}`}
            />
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'POWER',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Công suất
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              kW
            </span>
          </div>
        ),
        selector: (row) => Utils.formatNumber(row?.power / 1000),
        minWidth: '100px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONNECTOR_NUM',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Số súng sạc
          </span>
        ),
        selector: (row) => row?.chargeConnectors?.length,
        minWidth: '90px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONNECTOR_1_STATUS',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạng thái súng sạc 1
          </span>
        ),
        selector: (row) => {
          const connector =
            _.find(row?.chargeConnectors, (c) => c?.connectorNumber === 1) ??
            row?.chargeConnectors?.[0];
          if (!connector) return <span></span>;
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargeConnectorStatusName(connector?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargeConnectorStatusColor(
                connector?.status
              )}`}
            />
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONNECTOR_2_STATUS',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạng thái súng sạc 2
          </span>
        ),
        selector: (row) => {
          const connector =
            _.find(row?.chargeConnectors, (c) => c?.connectorNumber === 2) ??
            row?.chargeConnectors?.[1];
          if (!connector) return <span></span>;
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargeConnectorStatusName(connector?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargeConnectorStatusColor(
                connector?.status
              )}`}
            />
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PRICING_MODE',
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Phương thức tính giá
          </span>
        ),
        selector: (row) =>
          row?.pricing?.pricingMode
            ? _.find(
              ChargeHelper.Data.chargePointPricingModeOptions,
              (item) => item?.value === row?.pricing?.pricingMode
            )?.name
            : 'Mặc định',
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PRICE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Giá sạc
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              VNĐ/kWh
            </span>
          </div>
        ),
        selector: (row) => {
          if (row?.pricing?.pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
            return (
              <span>
                {row?.pricing?.flatPrice !== null
                  ? Utils.formatNumber(row?.pricing?.flatPrice)
                  : 'Mặc định'}
              </span>
            );
          } else if (row?.pricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
            return (
              <span>
                BT: {Utils.formatNumber(row?.pricing?.normalPrice)}, CĐ{' '}
                {Utils.formatNumber(row?.pricing?.peakPrice)}, TĐ:{' '}
                {Utils.formatNumber(row?.pricing?.offPeakPrice)}
              </span>
            );
          }
          return <span>{'Mặc định'}</span>;
        },
        right: true,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'FREE_DURATION',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Thời gian chờ miễn phí
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              Phút
            </span>
          </div>
        ),
        selector: (row) =>
          row?.pricing
            ? Utils.formatNumber(row?.pricing?.freeOverchargeDuration / 60)
            : 'Mặc định',
        right: true,
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OVER_PRICE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Phí sạc quá giờ
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              VNĐ/phút
            </span>
          </div>
        ),
        selector: (row) =>
          row?.pricing ? Utils.formatNumber(row?.pricing?.overchargePrice) : 'Mặc định',
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'RESERVATION_PRICE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Giá đặt giữ chỗ
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              VNĐ
            </span>
          </div>
        ),
        selector: (row) =>
          row?.pricing ? Utils.formatNumber(row?.pricing?.reservationFee ?? 0) : 'Mặc định',
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'MAINTENANCE_SCHEDULE',
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Lịch bảo trì định kỳ
            </span>
          </div>
        ),
        selector: (row) => row?.maintenanceSchedule
          ? (
            <KTTooltip
              text={`<div>
              <span>Ngày bắt đầu: ${Utils.formatDateTimeToString(row?.maintenanceSchedule?.startDate, 'DD/MM/YYYY')}</span><br />
              <span>Chu kỳ: ${Utils.formatNumber(row?.maintenanceSchedule?.cycle)} ngày</span><br />
              <span>${row?.maintenanceSchedule?.description}</span>
              </div>`}
              textHeader={`Lịch bảo trì định kỳ: ${row?.serialNumber}`}
              isHtmlContent
            >
              <span style={{
                textDecoration: 'underline'
              }}>
                Chu kỳ {Utils.formatNumber(row?.maintenanceSchedule?.cycle)} ngày
              </span>
            </KTTooltip >
          )
          : '',
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Thao tác
          </span>
        ),
        width: isAdminAgency ? '100px' : '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        center: isAdminAgency,
        cell: (row) => {
          const actions = buildMoreActions(row);
          return UIHelper.buildMoreActionsButton(actions, true);
        },
      },
    ];
  }, [user, stationId, filter]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- METHODS ----
  const handleRefresh = (newFilter) => {
    setFilter({ ...newFilter });
  };

  const buildMoreActions = (row) => {
    const actions = [];
    // xem chi tiet
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointView.code)) {
      actions.push({
        text: 'Xem chi tiết',
        iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          if (!!stationId) {
            router.navigate(`point/${row?.pointId}`);
          } else {
            router.navigate(`${row?.pointId}`);
          }
        },
      });
    }
    // NDT nhuong quyen & NDT lien ket: chi cho xem chi tiet, bao loi va khoi dong lai tru sac
    if (isInvestor || isCoinvestor) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointUpdate.code)) {
        if (row.status !== ChargeConstants.ChargePointStatus.FAULT) {
          actions.push({
            text: 'Báo lỗi trụ sạc',
            iconElement: (
              <i className="fa-regular fa-triangle-exclamation" style={{ fontSize: '1.1rem' }} />
            ),
            click: () => {
              ChargeHelper.UI.showDialogConfirmReportErrorChargePoint(row, () => {
                handleRefresh(filter);
              });
            },
          });
        }
        if (
          [
            ChargeConstants.ChargePointStatus.DISABLE,
            ChargeConstants.ChargePointStatus.DISCONNECTED,
            ChargeConstants.ChargePointStatus.FAULT,
          ].includes(row.status)
        ) {
          actions.push({
            text: 'Khởi động lại trụ sạc',
            iconElement: <i className="fa-regular fa-rotate-right" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmRestartChargePoint(row, () => {
                handleRefresh(filter);
              });
            },
          });
        }
      }
      return actions;
    }
    if (!isAdminAgency) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointUpdate.code)) {
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            if (!!stationId) {
              router.navigate(`point/${row?.pointId}/edit`);
            } else {
              router.navigate(`${row?.pointId}/edit`);
            }
          },
        });
        // hoan thanh bao tri
        if (row.status === ChargeConstants.ChargePointStatus.MAINTAINING) {
          actions.push({
            text: 'Hoàn thành bảo trì',
            iconElement: (
              <i className="fa-regular fa-clipboard-check" style={{ fontSize: '1.1rem' }} />
            ),
            click: () => {
              ChargeHelper.UI.showDialogConfirmCompleteMaintaining(row, () => {
                handleRefresh(filter);
              });
            },
          });
        }
        // thiet lap lich bao tri dinh ky
        actions.push({
          text: 'Thiết lập lịch bảo trì định kỳ',
          iconElement: (
            <i className="fa-regular fa-screwdriver-wrench" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            MaintainerHelper.UI.showDialogCreateMaintenanceSchedule(row, () => {
              handleRefresh(filter);
            });
          },
        });
        // di chuyen tru sac
        actions.push({
          text: 'Di chuyển trụ sạc',
          iconElement: (
            <i className="fa-regular fa-arrow-right-arrow-left" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            ChargeHelper.UI.showDialogConfirmMoveChargePoint(row, () => {
              handleRefresh(filter);
            });
          },
        });
        actions.push({
          text: 'Giới hạn công suất',
          iconElement: (
            <i className="fa-regular fa-arrows-from-dotted-line" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            ChargeHelper.UI.showDialogConfirmSetPowerLimitChargePoint(row, () => {
              handleRefresh(filter);
            });
          },
        });
        if (row.status === ChargeConstants.ChargePointStatus.FAULT) {
          actions.push({
            text: 'Xem mô tả lỗi',
            iconElement: (
              <i className="fa-regular fa-triangle-exclamation" style={{ fontSize: '1.1rem' }} />
            ),
            click: () => {
              ChargeHelper.UI.showDialogChargePointErrorDescription(row);
            },
          });
        }
        if (row.status !== ChargeConstants.ChargePointStatus.FAULT) {
          actions.push({
            text: 'Báo lỗi trụ sạc',
            iconElement: (
              <i className="fa-regular fa-triangle-exclamation" style={{ fontSize: '1.1rem' }} />
            ),
            click: () => {
              ChargeHelper.UI.showDialogConfirmReportErrorChargePoint(row, () => {
                handleRefresh(filter);
              });
            },
          });
        }
        actions.push({
          text: 'Cập nhật firmware',
          iconElement: <i className="fa-regular fa-arrows-rotate" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogUpdateFirmware(
              row,
              () => handleRefresh(filter),
              () => {
                if (!!stationId) router.navigate(`point/${row?.pointId}`);
                else router.navigate(`${row?.pointId}`);
              }
            );
          },
        });
        actions.push({
          text: 'Xem lịch sử cập nhật firmware',
          iconElement: <i className="fa-regular fa-clock-rotate-left" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogFirmwareHistory(row);
          },
        });
        if (row.status === ChargeConstants.ChargePointStatus.DISABLE) {
          actions.push({
            text: 'Khởi động lại trụ sạc',
            iconElement: <i className="fa-regular fa-rotate-right" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmRestartChargePoint(row, () => {
                handleRefresh(filter);
              });
            },
          });
        } else if (
          [
            ChargeConstants.ChargePointStatus.DISCONNECTED,
            ChargeConstants.ChargePointStatus.FAULT,
          ].includes(row.status)
        ) {
          actions.push({
            text: 'Khởi động lại trụ sạc',
            iconElement: <i className="fa-regular fa-rotate-right" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.showDialogConfirmRestartChargePoint(row, () => {
                handleRefresh(filter);
              });
            },
          });
        }
      }
      if (
        Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointUpdate.code) &&
        row.status !== ChargeConstants.ChargePointStatus.DISABLE
      ) {
        actions.push({
          text: 'Vô hiệu hóa trụ sạc',
          iconElement: <i className="fa-regular fa-ban" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmDeactivateChargePoint(row, () => {
              handleRefresh(filter);
            });
          },
        });
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointDelete.code)) {
        actions.push({
          text: 'Xoá',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmDeleteChargePoint(row, () => {
              // xoa tru sac thanh cong
              handleRefresh(filter);
            });
          },
        });
      }
    }
    return actions;
  };

  const updateFilter = (key, value) => {
    if (!!stationId) {
      const updatedFilter = { ...filter, [key]: value }
      setFilter(updatedFilter);
      setFilterDropdown(updatedFilter);
    } else {
      Global.g_filterChargePointList = { ...Global.g_filterChargePointList, [key]: value };
      setFilter(Global.g_filterChargePointList);
      setFilterDropdown(Global.g_filterChargePointList);
    }
  };

  const handleExportExcel = async () => {
    if (exporting || !filter) return;

    setExporting(true);
    try {
      const params = _.pickBy(
        {
          ...filter,
          page: 0,
          limit: Math.max(total, filter?.limit ?? 0, 9999),
          stationId: stationId ?? filter?.stationId,
          isTestChargePoint: isTestStation || filter?.isTestChargePoint,
        },
        (value) => value !== null && value !== undefined && value !== ''
      );
      const [{ chargePoints = [] }, pricingData] = await Promise.all([
        ChargeHelper.API.requestGetChargePointList(params),
        ChargeHelper.API.requestGetChargePricingList({ limit: 9999 }),
      ]);

      if (_.isEmpty(chargePoints)) {
        ToastHelper.showError('Không có dữ liệu để tải xuống');
        return;
      }

      const pricings = pricingData?.pricings ?? [];
      const exportItems = chargePoints.map((point) => {
        const pointPricing = pricings.find((pricing) => pricing.pointId === point.pointId);
        const operatorPricing = !pointPricing
          ? pricings.find(
            (pricing) =>
              pricing.operatorId === point.operatorId && !pricing.pointId && !pricing.agencyId
          )
          : null;
        return { ...point, pricing: pointPricing ?? operatorPricing ?? point.pricing };
      });

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Danh sách trụ sạc', {
        views: [{ state: 'frozen', ySplit: 2 }],
      });
      sheet.properties.defaultRowHeight = 20;
      sheet.pageSetup = {
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        paperSize: 9,
      };

      const columns = [
        { key: 'index', width: 7 },
        { key: 'pointCode', width: 18 },
        { key: 'pointName', width: 20 },
        { key: 'stationCode', width: 18 },
        { key: 'stationName', width: 28 },
        { key: 'ocppId', width: 20 },
        { key: 'phoneNumber', width: 18 },
        { key: 'modelName', width: 24 },
        { key: 'type', width: 14 },
        { key: 'status', width: 18 },
        { key: 'power', width: 15 },
        { key: 'connectorCount', width: 14 },
        { key: 'connector1Status', width: 18 },
        { key: 'connector2Status', width: 18 },
        { key: 'pricingMode', width: 20 },
        { key: 'price', width: 34 },
        { key: 'freeDuration', width: 18 },
        { key: 'overchargePrice', width: 18 },
        { key: 'reservationFee', width: 18 },
        { key: 'maintenanceSchedule', width: 31 },
      ];
      sheet.columns = columns;
      sheet.mergeCells('A1:T1');

      const from = filter?.from ? moment(filter.from) : moment();
      const to = filter?.to ? moment(filter.to) : moment();
      sheet.getCell('A1').value = `DANH SÁCH TRỤ SẠC`;
      sheet.getCell('A1').font = {
        name: 'Times New Roman',
        size: 11,
        bold: true,
        color: { argb: 'FFFFFFFF' },
      };
      sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
      sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF29618E' } };
      sheet.getRow(1).height = 25;

      const headers = [
        'STT',
        'Mã trụ sạc',
        'Tên trụ sạc',
        'Mã trạm sạc',
        'Tên trạm sạc',
        'Mã OCPP',
        'Số điện thoại',
        'Dòng trụ sạc',
        'Loại trụ sạc',
        'Trạng thái trụ',
        'Công suất (kW)',
        'Số súng sạc',
        'Trạng thái súng 1',
        'Trạng thái súng 2',
        'Phương thức tính giá',
        'Giá sạc (VNĐ/kWh)',
        'Thời gian chờ miễn phí (Phút)',
        'Phí sạc quá giờ (VNĐ/phút)',
        'Phí đặt giữ chỗ (VNĐ)',
        'Lịch bảo trì định kỳ',
      ];
      sheet.getRow(2).values = headers;
      sheet.getRow(2).height = 40;
      sheet.getRow(2).font = { name: 'Times New Roman', size: 10, bold: true };
      sheet.getRow(2).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

      exportItems.forEach((point, index) => {
        const connector1 =
          _.find(point?.chargeConnectors, (connector) => connector?.connectorNumber === 1) ??
          point?.chargeConnectors?.[0];
        const connector2 =
          _.find(point?.chargeConnectors, (connector) => connector?.connectorNumber === 2) ??
          point?.chargeConnectors?.[1];
        const pricing = point?.pricing;
        let price = '';
        if (pricing?.pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
          price = pricing?.flatPrice ?? '';
        } else if (pricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
          price = `BT: ${Utils.formatNumber(pricing?.normalPrice)}, CĐ: ${Utils.formatNumber(pricing?.peakPrice)}, TĐ: ${Utils.formatNumber(pricing?.offPeakPrice)}`;
        }
        const schedule = point?.maintenanceSchedule;

        sheet.addRow({
          index: index + 1,
          pointCode: point?.pointCode ?? '',
          pointName: point?.name ?? '',
          stationCode: point?.chargeStation?.stationCode ?? '',
          stationName: point?.chargeStation?.name ?? '',
          ocppId: point?.ocppId ?? '',
          phoneNumber: point?.phoneNumber ?? '',
          modelName: point?.chargeModel?.name ?? '',
          type: point?.type ?? '',
          status: ChargeHelper.Utils.getChargePointStatusName(point?.status),
          power: _.isFinite(_.toNumber(point?.power)) ? _.toNumber(point.power) / 1000 : '',
          connectorCount: point?.chargeConnectors?.length ?? 0,
          connector1Status: connector1
            ? ChargeHelper.Utils.getChargeConnectorStatusName(connector1.status)
            : '',
          connector2Status: connector2
            ? ChargeHelper.Utils.getChargeConnectorStatusName(connector2.status)
            : '',
          pricingMode: pricing?.pricingMode
            ? _.find(
              ChargeHelper.Data.chargePointPricingModeOptions,
              (item) => item.value === pricing.pricingMode
            )?.name ?? ''
            : 'Mặc định',
          price,
          freeDuration: pricing?.freeOverchargeDuration != null
            ? pricing.freeOverchargeDuration / 60
            : '',
          overchargePrice: pricing?.overchargePrice ?? '',
          reservationFee: pricing?.reservationFee ?? 0,
          maintenanceSchedule: schedule
            ? `Chu kỳ ${schedule.cycle} ngày, từ ${Utils.formatDateTimeToString(schedule.startDate, 'DD/MM/YYYY')}`
            : '',
        });
      });

      sheet.getCell('A1').border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' },
      };
      sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } },
          };
          if (rowNumber >= 3) {
            cell.font = { name: 'Times New Roman', size: 10 };
            cell.alignment = { vertical: 'middle', wrapText: true };
          }
        });
      });
      const applyColumnAlignment = (columnKeys, horizontal) => {
        columnKeys.forEach((columnKey) => {
          sheet.getColumn(columnKey).eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (rowNumber >= 2) {
              cell.alignment = { vertical: 'middle', horizontal, wrapText: true };
            }
          });
        });
      };
      applyColumnAlignment(['A'], 'center');
      applyColumnAlignment(
        ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'M', 'N', 'O', 'T'],
        'left'
      );
      applyColumnAlignment(['K', 'L', 'P', 'Q', 'R', 'S'], 'right');
      ['K', 'Q', 'R', 'S'].forEach((column) => {
        sheet.getColumn(column).numFmt = '#,##0';
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileDate = moment().format('DDMMYYYY');
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `Danh_sach_tru_sac_${fileDate}.xlsx`
      );
    } catch (error) {
      ToastHelper.showError(`Không thể tải danh sách trụ sạc: ${error?.message ?? 'Đã có lỗi xảy ra'}`);
    } finally {
      setExporting(false);
    }
  };

  const [selectedChargeStation, setSelectedChargeStation] = useState(null);

  const dropdownChargeStationEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="stationId"
        name="stationId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargeStation?.name ?? 'Tất cả'}
          </button>
        }
        selectedValue={selectedChargeStation}
        subTitleType="address"
        dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargeStationList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargeStations?.map((station) => ({
                id: station?.stationId,
                name: station?.name,
                item: station,
                address: ChargeHelper.Utils.getChargeStationFullAddress(station),
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargeStation(value);
          updateFilter('stationId', value?.id);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trạm sạc',
          role: 'STATION',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              updateFilter('stationId', '');
              setSelectedChargeStation(null);
            })}
          </div>
        }
      />
    )
  }, [selectedChargeStation])

  return (
    <div className="px-4">
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-3 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách trụ sạc
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} trụ sạc
                </span>
                <span style={{ color: AppColors.gray[600] }}> • </span>
                Số trụ sạc còn trống:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(availableTotal)}/{Utils.formatNumber(total)}
                </span>
              </span>
            </div>
            <div className="d-flex align-items-center gap-3">
              {UIHelper.buildBlueButton(
                'Tải xuống',
                handleExportExcel,
                <i className="fa-regular fa-download" />,
                false,
                exporting
              )}
              {isAdmin && !!stationId && enabledEdit && Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointCreate.code) &&
                UIHelper.buildGreenButton(
                  'Thêm trụ sạc',
                  () => {
                    router.navigate('point/add');
                  },
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="w-100 d-flex align-items-end flex-wrap pb-4 px-4">
              {/* tim kiem */}
              <div className={`${!!stationId ? 'flex-grow-1' : 'col-lg-3'} py-2 pr-0 pr-xl-4 pr-lg-4 pl-0`}>
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Theo mã trụ sạc, mã CPID, số điện thoại..."
                  value={filter?.q}
                  onSubmit={(text) => {
                    setFilter({ ...filter, q: text });
                  }}
                />
              </div>
              {/* loc theo tram sac */}
              {!!chargePointListFilter && (
                <div className={`flex-grow-1 py-2 pr-4 pt-1`}>
                  {UIHelper.buildFormLabel('Trạm sạc')}
                  {dropdownChargeStationEl}
                </div>
              )}

              {/* loc theo trang thai */}
              <KTDropdownFilter
                label='Trạng thái trụ'
                filterKey="status"
                options={[
                  { name: 'Tất cả', value: '' },
                  ...ChargeHelper.Data.chargePointStatusOptions,
                ]}
                selectedValue={filter?.status}
                additionalClassName={`${'pr-0 pr-0 pr-xl-4'}`}
                handleChange={(value) => {
                  updateFilter('status', value);
                }}
              />
              {/* loc theo loai tru sac */}
              <KTDropdownFilter
                label='Loại trụ sạc'
                filterKey="type"
                options={[
                  { name: 'Tất cả', value: '' },
                  ...ChargeHelper.Data.chargePointTypeOptions,
                ]}
                selectedValue={filter?.type}
                additionalClassName={`${'pl-0 pr-0 pr-xl-4'}`}
                handleChange={(value) => {
                  updateFilter('type', value);
                }}
              />
              {/* loc theo dong tru sac (chi hien thi trong tram sac) */}
              {!!stationId && stationModels.length > 0 && (
                <div className="flex-grow-1 py-2 pr-0 pr-xl-4">
                  {UIHelper.buildFormLabel('Dòng trụ sạc')}
                  <ModelTreeDropdown
                    value={filter?.modelId ?? ''}
                    brandList={stationBrands}
                    modelList={stationModels}
                    onChange={(modelId) => updateFilter('modelId', modelId)}
                  />
                </div>
              )}

              {/* bo loc */}
              {!!chargePointListFilter && (
                <div className="py-2 pr-4">
                  <DropdownFilterTable
                    filterComponent={[
                      <div>
                        <ChargePointListFilterView
                          isInDropdown={true}
                          chargePointListFilter={chargePointListFilter}
                          selectedChargeStation={selectedChargeStation}
                          setSelectedChargeStation={setSelectedChargeStation}
                          updateFilter={updateFilter}
                          filterDropdown={filterDropdown}
                          setFilterDropdown={setFilterDropdown}
                        />
                      </div>
                    ]}
                    resetDefaultTable={() => {
                      const updateFilterDropdown = {
                        ...filterDropdown,
                        page: 0,
                        limit: AppConfigs.defaultPageLimit,
                        q: '',
                        status: '',
                        type: '',
                        stationId: '',
                        vehicleType: '',
                        chargingSpeed: '',
                        modelId: '',
                        isTestChargePoint: 0,
                      };
                      setFilterDropdown(updateFilterDropdown);
                      setFilter(updateFilterDropdown);
                      setSelectedChargeStation('');
                    }}
                    saveFilter={() => {
                      setFilter(filterDropdown);
                      Global.g_filterChargePointList = filterDropdown;
                    }}
                    isDefaultFilter={Utils.checkDefaultFilter(
                      ['page', 'limit', 'landlordId', 'isTestChargePoint'],
                      filter,
                      dateFilter.tatCaThoiGian,
                      false
                    )}
                  />
                </div>
              )}
              {/* cau hinh cot */}
              <div className="pr-0 py-2">
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  saveChanges={saveChanges}
                  resetDefaultTableColumn={() => {
                    setFullColumnIds(defaultColumnIds);
                    resetDefaultTableColumn();
                  }}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={filteredItems}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Không có trụ sạc nào trong trạm sạc này.'
                )}
              />
            </div>
          </div>


          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter?.page + 1}
              rowCount={total}
              rowsPerPage={filter?.limit}
              currentItemInPageCount={filteredItems?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                setFilter({
                  ...filter,
                  page: iNewPage - 1,
                });
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                setFilter({
                  ...filter,
                  limit: iNewRowsPerPage,
                  page: 0,
                });
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="trụ sạc"
            />
          </div>
        </div>,
        false,
        false,
        true,
        false,
        AppColors.gray[300],
        hasAppTab
      )}
    </div>
  );
}

export default ChargePointListView;
